import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { styled, keyframes, css } from 'styled-components';
import THEME from '../theme.js';
import IconButton from './IconButton.js';
import X from './Icons/X.js';
const DEFAULT_HEADER_MARGIN = '0px 0px 20px 0px';
const Modal = (props) => {
    const lang = useSelector((s) => s.lang);
    const titleId = useId();
    const pressStartedOnBackdrop = useRef(false);
    const dialogRef = useRef(null);
    const { onToggle } = props;
    useEffect(() => {
        const onEscapePress = (evt) => {
            // A dropdown inside the modal marks the Escape it consumed as handled
            if (evt.code !== 'Escape' || evt.defaultPrevented) {
                return;
            }
            // Dialogs share a z-index, so the last one in the document is on top
            const dialogs = document.querySelectorAll('[role="dialog"]');
            if (dialogs[dialogs.length - 1] === dialogRef.current) {
                onToggle();
            }
        };
        document.addEventListener('keydown', onEscapePress);
        return () => {
            document.removeEventListener('keydown', onEscapePress);
        };
    }, [onToggle]);
    const referenceNode = props.reference && props.reference.current;
    const reference = referenceNode
        ? referenceNode.getBoundingClientRect()
        : null;
    const headerMargin = props.headerMargin ?? DEFAULT_HEADER_MARGIN;
    return createPortal(React.createElement(Backdrop, { $anchored: reference != null, onMouseDown: (e) => {
            pressStartedOnBackdrop.current = e.target === e.currentTarget;
        }, onClick: (e) => {
            // Portal events bubble to the components that render the modal
            e.stopPropagation();
            // A text selection dragged out of the modal ends on the backdrop
            if (pressStartedOnBackdrop.current && e.target === e.currentTarget) {
                props.onToggle();
            }
        } },
        React.createElement(Dialog, { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, $maxWidth: props.maxWidth, $reference: reference, $padding: props.padding, $fullscreen: props.fullscreen, onClick: (e) => e.stopPropagation() },
            React.createElement(Header, { $margin: headerMargin },
                React.createElement(Title, { id: titleId }, props.header),
                React.createElement(CloseButton, { type: "button", title: lang.misc.close, "aria-label": lang.misc.close, onClick: props.onToggle },
                    React.createElement(X, { size: 24 }))),
            React.createElement(Body, { $headingMargin: headerMargin, $reference: reference, $fullscreen: props.fullscreen }, props.children),
            props.footer && React.createElement(Footer, null, props.footer))), document.body);
};
export default Modal;
const fadeIn = keyframes `
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const slideIn = keyframes `
  from {
    transform: translateY(5px);
  }
  to {
    transform: translateY(0px);
  }
`;
const Backdrop = styled.div `
  background-color: ${(p) => p.$anchored ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.3)'};
  position: fixed;
  height: 100vh;
  width: 100vw;
  top: 0;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  animation: ${fadeIn} 0.1s ease-in;
  z-index: 100;
`;
const Dialog = styled.div `
  background-color: ${THEME.COLOR_SECONDARY};
  color: ${THEME.COLOR_PRIMARY};
  width: 100%;
  padding: ${(p) => p.$padding ?? `${THEME.GRID_SPACING}px`};
  border-radius: ${(p) => (p.$fullscreen ? '0' : '5px')};
  max-width: ${(p) => (p.$fullscreen ? '100%' : (p.$maxWidth ?? '900px'))};
  margin: ${(p) => (p.$fullscreen ? '0' : '70px auto 50px auto')};

  ${(p) => p.$fullscreen &&
    css `
      height: 100vh;
    `}

  ${(p) => p.$reference
    ? css `
          position: fixed;
          top: ${p.$reference.y + p.$reference.height + 15}px;
          margin-top: 0;
          right: calc(100vw - ${p.$reference.right + 10}px);

          /* Arrow pointing at the element the modal is anchored to */
          &::before {
            content: '';
            z-index: 1;
            height: 0;
            width: 0;
            position: absolute;
            right: ${p.$reference.width / 2}px;
            top: -20px;
            border: 10px solid ${THEME.COLOR_SECONDARY};
            border-right-color: transparent;
            border-top-color: transparent;
            border-left-color: transparent;
          }
        `
    : css `
          animation: ${slideIn} 100ms;
        `}
`;
const Header = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin: ${(p) => p.$margin};
`;
// The 32px line matches the close button, so they share a top edge
const Title = styled.h2 `
  flex: 1;
  min-width: 0;
  margin: 0;
  line-height: 32px;

  svg {
    float: right;
  }
`;
const CloseButton = styled(IconButton) `
  flex: none;
`;
const Body = styled.div `
  ${(p) => p.$fullscreen &&
    css `
      overflow-y: scroll;
      height: calc(100vh - 63px);
    `}

  ${(p) => p.$reference &&
    css `
      max-height: calc(100vh - ${p.$reference.y + p.$reference.height + 120}px);
      overflow-y: scroll;
      -webkit-overflow-scrolling: touch;
    `}

  h2 {
    margin: ${(p) => p.$headingMargin};

    svg {
      float: right;
    }
  }

  object,
  iframe {
    width: 100%;
    height: calc(100vh - 215px);
  }

  img {
    max-width: 100%;
  }
`;
const Footer = styled.div `
  border-top: 1px solid ${THEME.BORDER};
  padding: 15px 20px;
  margin: ${THEME.GRID_SPACING}px -${THEME.GRID_SPACING}px -${THEME.GRID_SPACING}px -${THEME.GRID_SPACING}px;
`;
//# sourceMappingURL=Modal.js.map